/**
 * Software Assurance Workflow Landing Page
 *
 * WHY: This is the entry point for the Software Assurance workflow within SoftwareIQ.
 * It provides clear calls-to-action for:
 * - Creating a new mission (starting a new software assurance project)
 * - Opening an existing mission (continuing work on an existing project)
 * - Quick access to workflow tools (Findings, Vetting, Schemas)
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '@/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  ShieldCheck,
  Plus,
  FolderOpen,
  Target,
  ArrowRight,
  Shield,
  ClipboardCheck,
  FileCode,
  Clock,
  Search,
  ListChecks,
} from 'lucide-react'

export function SoftwareAssurancePage() {
  const navigate = useNavigate()
  const [openMissionDialog, setOpenMissionDialog] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch missions for the "Open Mission" dialog
  const { data: missions = [], isLoading: missionsLoading } = useQuery({
    queryKey: ['platform', 'missions'],
    queryFn: () => api.platform.missions.list(),
  })

  // Filter missions by search
  const filteredMissions = missions.filter(m =>
    m.mission_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.mission_objective?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const activeMissions = missions.filter(m => m.status === 'active')

  const handleSelectMission = (missionId: string) => {
    setOpenMissionDialog(false)
    // Navigate to findings with mission context
    navigate(`/softwareiq/assurance/findings?mission=${missionId}`)
  }

  const handleCreateMission = () => {
    navigate('/missions?action=create')
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="p-2">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <ShieldCheck className="h-6 w-6 text-blue-400" />
            </div>
          </div>
          <h1 className="text-3xl font-display font-bold">Software Assurance</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            JFAC-compliant software vetting workflow for identifying, analyzing, and remediating
            security findings in mission-critical software systems.
          </p>
          <div className="mt-4">
            <Button
              variant="glow"
              onClick={() => navigate('/softwareiq/assurance/workflow')}
            >
              Launch JFAC Workflow
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Primary CTAs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create New Mission */}
        <Card className="hover:border-primary/50 transition-colors group cursor-pointer"
              onClick={handleCreateMission}>
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="p-4 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <Plus className="h-8 w-8 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">Create New Mission</h2>
                <p className="text-muted-foreground mb-4">
                  Start a new software assurance project. Define your mission scope,
                  configure scanners, and begin identifying security findings.
                </p>
                <Button variant="glow" className="group-hover:shadow-lg transition-shadow">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Open Existing Mission */}
        <Card className="hover:border-primary/50 transition-colors group cursor-pointer"
              onClick={() => setOpenMissionDialog(true)}>
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="p-4 rounded-xl bg-accent/10 group-hover:bg-accent/20 transition-colors">
                <FolderOpen className="h-8 w-8 text-accent-foreground" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">Open Existing Mission</h2>
                <p className="text-muted-foreground mb-4">
                  Continue working on an existing software assurance project.
                  Review findings, process vetting queue, or export reports.
                </p>
                <div className="flex items-center gap-3">
                  <Button variant="outline" className="group-hover:border-primary/50 transition-colors">
                    Browse Missions
                    <FolderOpen className="ml-2 h-4 w-4" />
                  </Button>
                  {activeMissions.length > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {activeMissions.length} active mission{activeMissions.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Access Tools */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Workflow Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <CardTitle className="text-base">Findings</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="mb-4">
                View and analyze security findings across all missions
              </CardDescription>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/softwareiq/assurance/findings">
                  View Findings
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-yellow-500" />
                <CardTitle className="text-base">Vetting Queue</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="mb-4">
                Process observations awaiting SME review and vetting
              </CardDescription>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/softwareiq/assurance/vetting">
                  Review Queue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <FileCode className="h-5 w-5 text-green-500" />
                <CardTitle className="text-base">Schemas</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="mb-4">
                Manage scanner configurations and data schemas
              </CardDescription>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/softwareiq/assurance/schemas">
                  View Schemas
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-base">STIG Applicability</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="mb-4">
                Sync STIG catalog and document mission applicability
              </CardDescription>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/softwareiq/assurance/stigs">
                  Review STIGs
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Missions Quick Access */}
      {activeMissions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Active Missions</h2>
            <Button asChild variant="ghost" size="sm">
              <Link to="/missions">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeMissions.slice(0, 3).map((mission) => (
              <Card
                key={mission.mission_id}
                className="hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => handleSelectMission(mission.mission_id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    <CardTitle className="text-sm font-medium truncate">
                      {mission.mission_name}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {mission.mission_objective || 'No description'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>Created {new Date(mission.created_at).toLocaleDateString()}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Open Mission Dialog */}
      <Dialog open={openMissionDialog} onOpenChange={setOpenMissionDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Open Mission
            </DialogTitle>
            <DialogDescription>
              Select a mission to continue your software assurance work
            </DialogDescription>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search missions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Mission List */}
          <div className="max-h-[300px] overflow-y-auto space-y-2">
            {missionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredMissions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? 'No missions match your search' : 'No missions available'}
              </div>
            ) : (
              filteredMissions.map((mission) => (
                <button
                  key={mission.mission_id}
                  onClick={() => handleSelectMission(mission.mission_id)}
                  className="w-full p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-all text-left"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-primary flex-shrink-0" />
                        <span className="font-medium truncate">{mission.mission_name}</span>
                      </div>
                      {mission.mission_objective && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                          {mission.mission_objective}
                        </p>
                      )}
                    </div>
                    <Badge
                      variant={mission.status === 'active' ? 'default' : 'secondary'}
                      className="flex-shrink-0"
                    >
                      {mission.status}
                    </Badge>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Create New Option */}
          <div className="pt-4 border-t border-border">
            <Button onClick={handleCreateMission} variant="outline" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Create New Mission
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
